import { useEffect, useRef } from 'react';
import { getImagePath } from '../utils/imagePath';
import EditableText from './EditableText';
import { useContent } from '../context/ContentContext';

const services = [
  {
    id: 'service-p2p',
    title: "PEER TO PEER TEXTING",
    description: "Reach people quickly, boost response rates, and drive action. Peer-to-peer texting delivers personalized, one-to-one messages that spark real conversations.",
    features: [
      "SMS or MMS",
      "High Resolution Videos",
      "Link Tracking",
      "Message Personalization",
      "List Management",
      "10DLC Registration"
    ],
    image: getImagePath("/images/services/p2p-blurred-outline.png"),
    side: 'left'
  },
  {
    id: 'service-auto-call',
    title: "Automated Phone Calls",
    description: "Share important updates, reminders, and calls to action with consistent messaging and reliable reach. Automated phone calls deliver pre-recorded messages quickly and efficiently to large audiences.",
    features: [
      "Attested CallerIDs",
      "Compliance & Opt-Out Management",
      "Detailed Call Reporting",
      "Professional Voice Options",
      "Human Quality Control"
    ],
    image: getImagePath("/images/services/auto-phone-call.png"),
    side: 'right'
  },
  {
    id: 'service-survey',
    title: "Surveys and Polling",
    description: "Gather feedback, measure sentiment, and understand your audience. Reach people where they're most responsive and collect reliable actionable insights.",
    features: [
      "Real-Time Reporting",
      "Custom Question Design",
      "Inbound & Outbound IVR",
      "Skip Logic & Branching",
      "",
      "Compliance & Opt-Out Management"
    ],
    image: getImagePath("/images/services/survey-and-poll-data.png"),
    side: 'left'
  },
  {
    id: 'service-agent',
    title: "Live Agent Calls",
    description: "Connect with your audience through real conversations. Our live agent calling service puts trained representatives on the line to answer questions, gather feedback, and deliver personalized outreach at scale.",
    features: [
      "Dedicated agent support",
      "Custom call scripts and talking points",
      "Real-time reporting and call monitoring",
      "Flexible scheduling and volume options",
      "Compliance and opt-out management"
    ],
    image: getImagePath("/images/services/liveagentcall-cleaned.png"),
    side: 'right'
  },
  {
    id: 'service-tele-townhall',
    title: "Tele-Townhalls",
    summary: "Host impactful large donor conference calls to connect with up to 50,000 supporters per minute. Empower your non-profit clients to engage with their donors in a meaningful way and foster a sense of community among supporters.",
    features: [
      "Connect with up to 50,000 supporters per minute",
      "Enhanced transparency and accountability",
      "Increased donor engagement",
      "Real-time feedback and support"
    ],
    image: getImagePath("/images/services/teletownhall.png"),
    side: 'left'
  },
  {
    id: 'service-data',
    title: "Registered Voter, Donor & Consumer Data",
    description: "Accurate, up-to-date information to help organizations target, understand, and reach the right audiences.",
    features: [
      "Enriched Phone Records",
      "Demographic Filters",
      "",
      "Data Modeling"
    ],
    image: getImagePath("/images/services/voter_sheet.png"),
    side: 'right'
  }
];

const Services = () => {
  const servicesListRef = useRef(null);
  const { getText, setText, isEditMode } = useContent();

  useEffect(() => {
    // Apply reveal animation to section title
    const sectionTitle = document.querySelector('#services h2');
    if (sectionTitle) {
      sectionTitle.classList.add('reveal');
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      });
      revealObserver.observe(sectionTitle);
    }

    // Apply slide-in animations to service cards (trigger earlier + faster animation on mobile)
    if (servicesListRef.current) {
      const serviceCards = servicesListRef.current.querySelectorAll('[data-service-card]');
      const isMobile = window.innerWidth < 768;

      const slideInObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('slide-in');
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateX(0)';
            slideInObserver.unobserve(entry.target);
          }
        });
      }, isMobile
        ? { threshold: 0.05, rootMargin: '0px 0px 120px 0px' }  // trigger ~120px before card enters viewport
        : { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
      );

      serviceCards.forEach(card => {
        slideInObserver.observe(card);
      });
    }
  }, []);

  return (
    <section
      id="services"
      className="py-12 md:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 m-0 bg-[var(--bg-charcoal)] relative overflow-hidden z-[3] w-full max-w-[100vw]"
    >
      <div className="!max-w-[1188px] mx-auto w-full overflow-hidden">
        {/* Section Title */}
        <h2
          className="text-center text-3xl md:text-4xl lg:text-5xl mb-8 md:mb-12 lg:mb-16 text-[var(--bg-pure-white)] relative block w-full font-semibold uppercase tracking-[4px] font-serif border-b-0 pb-6 after:content-[''] after:absolute after:-bottom-[10px] after:left-1/2 after:-translate-x-1/2 after:w-[150px] sm:after:w-[200px] md:after:w-[250px] after:h-[4px] sm:after:h-[5px] md:after:h-[6px] after:bg-[var(--accent-color)]"
        >
          <EditableText path="services.sectionTitle" fallback="Our Services" />
        </h2>

        {/* Services List - flex column layout */}
        <div 
          ref={servicesListRef}
          className="flex flex-col gap-8 md:gap-10 lg:gap-12 mt-0 relative z-[1] w-full items-center"
        >
          {services.map((service) => {
            const isLeft = service.side === 'left';
            
            return (
              <div 
                key={service.id}
                id={service.id}
                data-service-card
                className={`
                  bg-[var(--bg-pure-white)] text-[var(--text-dark)] p-0 rounded-xl
                  transition-all duration-300 md:duration-[800ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer
                  relative overflow-hidden border border-[var(--border-light)] z-[1]
                  shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)]
                  flex items-stretch w-full max-w-[1188px] mx-auto gap-0 opacity-0
                  flex-col md:flex-row
                  ${isLeft ? 'md:flex-row md:-translate-x-[100px]' : 'md:flex-row-reverse md:translate-x-[100px]'}
                  ${service.id === 'service-survey' || service.id === 'service-p2p' || service.id === 'service-agent' || service.id === 'service-auto-call' || service.id === 'service-tele-townhall' ? 'md:items-stretch' : 'min-h-[350px]'}
                `}
                onMouseEnter={(e) => {
                  if (e.currentTarget.classList.contains('slide-in')) {
                    e.currentTarget.style.transform = 'translateY(-6px) translateX(0)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget.classList.contains('slide-in')) {
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                {/* Service Image */}
                <div className={`
                  w-full flex-shrink-0
                  ${service.id === 'service-survey' 
                    ? 'md:w-[500px] md:min-w-[500px] md:max-w-[500px] md:self-stretch min-h-[350px] relative' 
                    : service.id === 'service-p2p'
                    ? 'md:w-[650px] md:min-w-[650px] md:max-w-[650px] md:self-stretch min-h-[350px] relative'
                    : service.id === 'service-agent'
                    ? 'md:w-[650px] md:min-w-[650px] md:max-w-[650px] md:self-stretch min-h-[350px] relative'
                    : service.id === 'service-auto-call'
                    ? 'md:w-[550px] md:min-w-[550px] md:max-w-[550px] md:self-stretch min-h-[350px] relative'
                    : service.id === 'service-tele-townhall'
                    ? 'md:w-[550px] md:min-w-[550px] md:max-w-[550px] md:self-stretch min-h-[350px] relative'
                    : service.id === 'service-data'
                    ? 'md:w-[550px] md:min-w-[550px] md:max-w-[550px] h-full min-h-[350px]'
                    : 'md:w-[380px] md:min-w-[380px] md:max-w-[380px] h-full min-h-[350px]'}
                  ${isLeft ? 'rounded-l-xl rounded-r-0 overflow-hidden' : 'rounded-r-xl rounded-l-0 overflow-hidden'}
                `}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`
                      block
                      ${service.id === 'service-survey' 
                        ? 'absolute inset-0 w-full h-full object-cover object-center' 
                        : service.id === 'service-tele-townhall'
                        ? 'absolute inset-0 w-full h-full object-cover object-right'
                        : service.id === 'service-p2p' || service.id === 'service-agent' || service.id === 'service-auto-call'
                        ? 'absolute inset-0 w-full h-full object-cover object-center'
                        : 'w-full h-full object-cover'}
                    `}
                  />
                </div>

                {/* Service Content */}
                <div 
                  className={`
                    flex-1 p-5 md:p-6 lg:p-8 flex flex-col
                    ${service.id === 'service-data' ? 'pt-0 md:pt-4 lg:pt-6' : ''}
                    ${service.id === 'service-tele-townhall' ? 'justify-start' : 'justify-center'}
                    ${service.id === 'service-tele-townhall' ? 'pb-5 md:pb-6 lg:pb-8' : ''}
                    ${service.id === 'service-survey' ? 'md:justify-start md:overflow-y-auto md:self-stretch md:h-full' : ''}
                    ${service.id === 'service-p2p' || service.id === 'service-agent' || service.id === 'service-auto-call' || service.id === 'service-tele-townhall' ? 'md:self-stretch md:h-full' : ''}
                  `}
                >
                  {/* Service Title */}
                  <h3 className="text-xl md:text-2xl lg:text-3xl mb-1 mt-0 font-semibold uppercase tracking-[2px] text-[var(--text-dark)]">
                    <EditableText path={`services.${service.id}.title`} fallback={service.title} />
                  </h3>

                  {/* Service Summary (for tele-townhall) */}
                  {(service.summary || getText(`services.${service.id}.summary`)) && (
                    <p className="text-[var(--text-medium)] leading-[1.7] text-base md:text-lg mb-6 md:mb-10 lg:mb-10 mt-0 pt-0">
                      <EditableText path={`services.${service.id}.summary`} fallback={service.summary ?? ''} />
                    </p>
                  )}

                  {/* Service Description */}
                  {(service.description || getText(`services.${service.id}.description`)) && (
                    <p className={`text-[var(--text-medium)] leading-[1.7] text-sm md:text-base mt-2 ${(service.features && service.features.length > 0) || getText(`services.${service.id}.feature0`) ? 'mb-4 md:mb-6' : ''}`}>
                      <EditableText path={`services.${service.id}.description`} fallback={service.description ?? ''} />
                    </p>
                  )}

                  {/* Service Features List (for tele-townhall and other services) */}
                  {(() => {
                    const maxFeatures = 15;
                    const featureItems = [];
                    for (let i = 0; i < maxFeatures; i++) {
                      const fromConfig = getText(`services.${service.id}.feature${i}`);
                      const text = fromConfig !== undefined ? fromConfig : (service.features?.[i] ?? '');
                      if (text === '' || text === undefined) continue;
                      featureItems.push({ i, text });
                    }
                    const nextEmptyIndex = (() => {
                      for (let j = 0; j < maxFeatures; j++) {
                        const t = getText(`services.${service.id}.feature${j}`);
                        if (t === undefined && !service.features?.[j]) return j;
                        if (t === '') return j;
                      }
                      return maxFeatures;
                    })();
                    const canAdd = isEditMode && nextEmptyIndex < maxFeatures;
                    if (featureItems.length === 0 && !canAdd) return null;
                    return (
                      <div className="mt-0 mb-0">
                        <ul className="list-none p-0 m-0 text-[var(--text-medium)] leading-[1.8] text-base md:text-lg mt-0 mb-0">
                          {featureItems.map(({ i, text }) => (
                            <li
                              key={i}
                              className="relative pl-9 mb-3 min-h-5 flex items-center text-base md:text-lg leading-[1.25rem] pt-0 last:mb-0
                                before:content-['✓'] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-5 before:bg-[var(--accent-color)] before:text-[var(--text-dark)]
                                before:flex before:items-center before:justify-center before:text-sm before:font-bold
                                before:rounded before:leading-none before:z-[1]
                                after:content-[''] after:absolute after:left-0 after:top-1/2 after:-translate-y-1/2 after:h-5 after:w-full after:bg-[var(--bg-light)] after:rounded after:z-[-1]
                              "
                            >
                              <span className="flex items-center gap-2 min-h-5">
                                <span className="flex-1 min-w-0">
                                  <EditableText path={`services.${service.id}.feature${i}`} fallback={text} />
                                </span>
                                {isEditMode && (
                                  <button
                                    type="button"
                                    onClick={() => setText(`services.${service.id}.feature${i}`, '')}
                                    className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-[var(--text-dark)] hover:bg-red-100 hover:text-red-700 text-sm font-bold transition-colors"
                                    aria-label="Remove bullet"
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {canAdd && (
                          <button
                            type="button"
                            onClick={() => setText(`services.${service.id}.feature${nextEmptyIndex}`, 'New bullet point')}
                            className="mt-2 text-sm font-semibold text-[#9a83ad] hover:text-[#8a73a0] transition-colors flex items-center gap-1"
                          >
                            + Add bullet point
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </section>
  );
};

export default Services;
